import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pipeline, PipelineStage, Client, pipelineAPI, clientsAPI } from '../../services/api';
import {
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ClientStageModal from './ClientStageModal';
import { Card, CardBody, Button, Badge, Spinner } from '../ui';

interface ClientPipelineBoardProps {
  isAdminContext?: boolean;
}

// Filter options
const LIFECYCLE_STAGES = [
  { value: '', label: 'All Lifecycle Stages' },
  { value: 'SUBSCRIBER', label: 'Subscriber' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'MQL', label: 'MQL' },
  { value: 'SQL', label: 'SQL' },
  { value: 'OPPORTUNITY', label: 'Opportunity' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'EVANGELIST', label: 'Evangelist' },
  { value: 'OTHER', label: 'Other' },
];

const LEAD_STATUSES = [
  { value: '', label: 'All Lead Statuses' },
  { value: 'NEW', label: 'New' },
  { value: 'ATTEMPTING_TO_CONTACT', label: 'Attempting to Contact' },
  { value: 'CONNECTED', label: 'Connected' },
  { value: 'OPEN_DEAL', label: 'Open Deal' },
  { value: 'UNQUALIFIED', label: 'Unqualified' },
  { value: 'BAD_TIMING', label: 'Bad Timing' },
  { value: 'LOST', label: 'Lost' },
];

const IMPORTANCE_OPTIONS = [
  { value: '', label: 'All Importance' },
  { value: 'VERY_HIGH', label: 'Very High' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
  { value: 'VERY_LOW', label: 'Very Low' },
];

const CLIENT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'COMPANY', label: 'Company' },
];

const ClientPipelineBoard: React.FC<ClientPipelineBoardProps> = ({ isAdminContext = true }) => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drag and drop state
  const [draggedClient, setDraggedClient] = useState<Client | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<number | null>(null);

  // Modal states
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLifecycleStage, setFilterLifecycleStage] = useState('');
  const [filterLeadStatus, setFilterLeadStatus] = useState('');
  const [filterImportance, setFilterImportance] = useState('');
  const [filterClientType, setFilterClientType] = useState('');

  useEffect(() => {
    loadPipelines();
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      loadStages();
      loadClients();
    }
  }, [selectedPipeline]);

  const loadPipelines = async () => {
    try {
      const data = await pipelineAPI.getAll();
      const activePipelines = data.filter(p => p.isActive);
      setPipelines(activePipelines);

      const defaultPipeline = activePipelines.find(p => p.isDefault) || activePipelines[0];
      setSelectedPipeline(defaultPipeline);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pipelines');
    }
  };

  const loadStages = async () => {
    if (!selectedPipeline) return;
    try {
      const data = await pipelineAPI.getStages(selectedPipeline.id);
      setStages(data);
    } catch (err: any) {
      console.error('Failed to load stages:', err);
    }
  };

  const loadClients = async () => {
    if (!selectedPipeline) return;
    try {
      setLoading(true);
      const data = await clientsAPI.getAll({ limit: 1000 });
      // Filter clients for this pipeline
      const pipelineClients = data.data.filter(
        (c: Client) => c.pipelineId === selectedPipeline.id
      );
      setClients(pipelineClients);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on all filter criteria
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Search filter (name, email, phone)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          (client.name?.toLowerCase().includes(query)) ||
          (client.email?.toLowerCase().includes(query)) ||
          (client.phoneNumbers?.some(p => p.toLowerCase().includes(query)));
        if (!matchesSearch) return false;
      }

      // Lifecycle stage filter
      if (filterLifecycleStage && client.lifecycleStage !== filterLifecycleStage) {
        return false;
      }

      // Lead status filter
      if (filterLeadStatus && client.leadStatus !== filterLeadStatus) {
        return false;
      }

      // Importance filter
      if (filterImportance && client.importance !== filterImportance) {
        return false;
      }

      // Client type filter
      if (filterClientType && client.type !== filterClientType) {
        return false;
      }

      return true;
    });
  }, [clients, searchQuery, filterLifecycleStage, filterLeadStatus, filterImportance, filterClientType]);

  const getClientsByStage = (stageId: number) => {
    return filteredClients.filter(c => c.stageId === stageId);
  };

  const hasActiveFilters = searchQuery || filterLifecycleStage || filterLeadStatus || filterImportance || filterClientType;

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterLifecycleStage('');
    setFilterLeadStatus('');
    setFilterImportance('');
    setFilterClientType('');
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, client: Client) => {
    setDraggedClient(client);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedClient(null);
    setDragOverStageId(null);
  };

  const handleDragOver = (e: React.DragEvent, stageId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStageId(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStageId(null);
  };

  const handleDrop = async (e: React.DragEvent, toStageId: number) => {
    e.preventDefault();
    setDragOverStageId(null);

    if (!draggedClient || draggedClient.stageId === toStageId) {
      setDraggedClient(null);
      return;
    }

    try {
      await clientsAPI.moveStage(draggedClient.id, toStageId);
      // Optimistic update
      setClients(prev =>
        prev.map(c =>
          c.id === draggedClient.id ? { ...c, stageId: toStageId } : c
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to move client');
      loadClients(); // Reload on error
    }
    setDraggedClient(null);
  };

  const handleClientClick = (client: Client) => {
    const basePath = isAdminContext ? '/admin' : '';
    navigate(`${basePath}/clients/${client.id}`);
  };

  const handleMoveClient = (client: Client) => {
    setSelectedClient(client);
    setShowStageModal(true);
  };

  const handleStageModalClose = () => {
    setShowStageModal(false);
    setSelectedClient(null);
    loadClients();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ORGANIZATION':
      case 'COMPANY':
        return <BuildingOfficeIcon className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  const getImportanceVariant = (importance: string): 'error' | 'warning' | 'success' | 'neutral' => {
    const variants: Record<string, 'error' | 'warning' | 'success' | 'neutral'> = {
      VERY_HIGH: 'error',
      HIGH: 'warning',
      MEDIUM: 'warning',
      LOW: 'success',
      VERY_LOW: 'neutral',
    };
    return variants[importance] || 'neutral';
  };

  if (loading && !selectedPipeline) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="text-xl font-bold text-neutral-900">Client Pipeline</h2>
            <p className="text-sm text-neutral-500">
              Drag and drop clients between stages
              {hasActiveFilters && (
                <span className="ml-2 text-primary-600">
                  ({filteredClients.length} of {clients.length} clients)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-48 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <Button
            variant={hasActiveFilters ? 'primary' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<FunnelIcon className="h-4 w-4" />}
          >
            Filters
            {hasActiveFilters && (
              <span className="ml-1 bg-white text-primary-600 text-xs rounded-full px-1.5">
                {[filterLifecycleStage, filterLeadStatus, filterImportance, filterClientType].filter(Boolean).length}
              </span>
            )}
          </Button>

          {/* Pipeline Selector */}
          <select
            value={selectedPipeline?.id || ''}
            onChange={(e) => {
              const pipeline = pipelines.find(p => p.id === parseInt(e.target.value));
              setSelectedPipeline(pipeline || null);
            }}
            className="block w-48 pl-3 pr-10 py-2 text-base border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-lg transition-colors"
          >
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card variant="bordered" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-neutral-700">Filter Clients</h3>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary-600 hover:text-primary-800 transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Lifecycle Stage</label>
              <select
                value={filterLifecycleStage}
                onChange={(e) => setFilterLifecycleStage(e.target.value)}
                className="block w-full border border-neutral-300 rounded-lg shadow-sm text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                {LIFECYCLE_STAGES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Lead Status</label>
              <select
                value={filterLeadStatus}
                onChange={(e) => setFilterLeadStatus(e.target.value)}
                className="block w-full border border-neutral-300 rounded-lg shadow-sm text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                {LEAD_STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Importance</label>
              <select
                value={filterImportance}
                onChange={(e) => setFilterImportance(e.target.value)}
                className="block w-full border border-neutral-300 rounded-lg shadow-sm text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                {IMPORTANCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Client Type</label>
              <select
                value={filterClientType}
                onChange={(e) => setFilterClientType(e.target.value)}
                className="block w-full border border-neutral-300 rounded-lg shadow-sm text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                {CLIENT_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Board */}
      <div className="overflow-x-auto pb-4">
        <div className="inline-flex space-x-4 min-w-full">
          {stages.map((stage) => {
            const stageClients = getClientsByStage(stage.id);
            const isDropTarget = dragOverStageId === stage.id;

            return (
              <div
                key={stage.id}
                className={`flex-shrink-0 w-80 rounded-xl transition-all duration-200 ${
                  isDropTarget
                    ? 'bg-primary-50 ring-2 ring-primary-400'
                    : 'bg-neutral-50'
                }`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="px-4 py-3 border-b border-neutral-200 bg-white rounded-t-xl">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      {stage.color && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        />
                      )}
                      <h3 className="text-sm font-semibold text-neutral-900">
                        {stage.name}
                      </h3>
                      <Badge variant="neutral" size="sm">
                        {stageClients.length}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Clients List */}
                <div className="p-3 space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto min-h-[100px]">
                  {stageClients.length === 0 ? (
                    <div className="text-center py-8 text-neutral-400 text-sm">
                      {isDropTarget ? 'Drop here' : 'No clients in this stage'}
                    </div>
                  ) : (
                    stageClients.map((client) => (
                      <Card
                        key={client.id}
                        variant="elevated"
                        hoverable
                        className={`cursor-grab active:cursor-grabbing ${
                          draggedClient?.id === client.id ? 'opacity-50' : ''
                        }`}
                        draggable
                        onDragStart={(e: React.DragEvent) => handleDragStart(e, client)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleClientClick(client)}
                      >
                        <CardBody className="p-3">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-2">
                                {getTypeIcon(client.type)}
                                <h4 className="text-sm font-medium text-neutral-900 line-clamp-1">
                                  {client.name}
                                </h4>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveClient(client);
                                }}
                                className="text-neutral-400 hover:text-primary-600 ml-2 transition-colors"
                                title="Move to another stage"
                              >
                                <ArrowsRightLeftIcon className="h-4 w-4" />
                              </button>
                            </div>

                            <Badge variant={getImportanceVariant(client.importance)} size="sm">
                              {client.importance.replace('_', ' ')}
                            </Badge>

                            {client.email && (
                              <div className="flex items-center text-xs text-neutral-600">
                                <EnvelopeIcon className="h-3 w-3 mr-1" />
                                <span className="truncate">{client.email}</span>
                              </div>
                            )}

                            {client.phoneNumbers && client.phoneNumbers.length > 0 && (
                              <div className="flex items-center text-xs text-neutral-500">
                                <PhoneIcon className="h-3 w-3 mr-1" />
                                <span>{client.phoneNumbers[0]}</span>
                              </div>
                            )}

                            {client.assignedUser && (
                              <div className="flex items-center text-xs text-neutral-500">
                                <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center mr-1">
                                  <span className="text-primary-600 font-medium text-xs">
                                    {client.assignedUser.firstName?.[0] || client.assignedUser.email[0].toUpperCase()}
                                  </span>
                                </div>
                                <span className="truncate">
                                  {client.assignedUser.firstName || client.assignedUser.email}
                                </span>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {showStageModal && selectedClient && (
        <ClientStageModal
          client={selectedClient}
          stages={stages}
          onClose={handleStageModalClose}
        />
      )}
    </div>
  );
};

export default ClientPipelineBoard;
